import { captureException } from '@sentry/react';
import type { z } from 'zod';

import { HttpError, apiErrorFrom, createApiClient, parseOrThrow } from '../../../api';
import type { ApiClient } from '../../../api';

import { invalidRequestOf } from './requestSchemas';

export const ANALYSIS_TIMEOUT_MS = 60_000;
export const APPLY_TIMEOUT_MS = 120_000;

const SERVER_FAILURE = 500;

const client = createApiClient({ toError: apiErrorFrom });

export const reportFailure = (error: unknown, extra: Record<string, unknown> = {}): void => {
  captureException(error, { tags: { feature: 'ctl-config' }, extra });
};

export const reportingFailures = <T>(read: () => T, extra: Record<string, unknown>): T => {
  try {
    return read();
  } catch (error) {
    reportFailure(error, extra);
    throw error;
  }
};

export const parsed = <S extends z.ZodTypeAny>(schema: S, raw: unknown, label: string): z.infer<S> =>
  reportingFailures(() => parseOrThrow(schema, raw, label), { label });

export const checkedBody = <S extends z.ZodTypeAny>(schema: S, body: unknown, label: string): z.output<S> => {
  const result = schema.safeParse(body);
  if (result.success) return result.data;
  const error = invalidRequestOf(label, result.error.issues);
  reportFailure(error, { label });
  throw error;
};

export const request: ApiClient['request'] = async (method, url, options) => {
  try {
    return await client.request(method, url, options);
  } catch (error) {
    if (error instanceof HttpError && error.status >= SERVER_FAILURE) reportFailure(error, { method, url });
    throw error;
  }
};
